/**
 * «Структура посевных площадей» (СПП) планового сезона — экран 2(в) годового
 * планирования.
 *
 * Макет (утв. founder 18.08):
 *   `docs/superpowers/prototypes/annual-planning/v2-02-spp.html`
 *   (Confluence 133955585 — пакет wireframes планирования-2027).
 *
 * Что экран обещает пользователю:
 *   • что и на какой площади хозяйство сеет в плановом сезоне — свод по
 *     культурам с долей от ВСЕЙ пашни;
 *   • сравнение с фактом прошлого сезона: что выросло, что ужалось, что ушло
 *     из структуры совсем;
 *   • замечания севооборота — с объяснением, но без запрета.
 *
 * Ключевые решения founder, из-за которых экран выглядит именно так:
 *   1. **СПП — срез варианта плана, а не сущность.** Своего редактирования тут
 *      нет: меняешь распределение культур или назначения техкарт — структура
 *      пересобирается сама. Поэтому экран читающий.
 *   2. **Нераспределённые гектары из знаменателя НЕ прячем.** Доли считаются
 *      от всей пашни, и неполный план честно выглядит неполным.
 *   3. **Севооборот объясняет, но не блокирует.** Решение оставить или
 *      переиграть — за агрономом.
 *
 * Вариант плана ведётся по ХОЗЯЙСТВУ (ADR-045 D3), поэтому у экрана два
 * переключателя: хозяйство и вариант плана. Оба живут в адресе страницы —
 * ссылку на «структуру варианта X» можно послать.
 */
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { TileGrid } from '@/components/ui/tile-grid';
import { describeApiError } from '@/lib/apiError';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import { CreateVariantButton } from '../CreateVariantButton';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';
import { RenameVariantButton } from '../RenameVariantButton';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import { StructureTable } from './StructureTable';
import {
  cropsSharePct,
  economicsHiddenNote,
  formatHa,
  formatTons,
  overplannedNote,
  planSourceNote,
  rotationBasisNote,
  useCropStructure,
  useSppPlanVariants,
  type CropStructure,
} from './sppApi';

interface SppSearch {
  farm?: string;
  variant?: string;
}

function Tile({
  value,
  label,
  tone,
}: {
  value: string;
  label: string;
  tone?: 'warn';
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div
          className={
            tone === 'warn'
              ? 'text-2xl font-semibold text-accent-brand'
              : 'text-2xl font-semibold text-foreground'
          }
        >
          {value}
        </div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function ChipRow({
  label,
  options,
  activeId,
  onPick,
  trailing,
}: {
  label: string;
  options: { id: string; name: string; badge?: string }[];
  activeId: string | null;
  onPick: (id: string) => void;
  /** Действие над АКТИВНЫМ чипом — например «переименовать вариант» (AK-719). */
  trailing?: React.ReactNode;
}) {
  if (options.length <= 1 && !options.some((o) => o.badge)) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          aria-pressed={option.id === activeId}
          onClick={() => onPick(option.id)}
          className={
            option.id === activeId
              ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors'
              : 'rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
          }
        >
          {option.name}
          {option.badge && (
            <span className="ml-2 text-xs font-normal">{option.badge}</span>
          )}
        </button>
      ))}
      {trailing}
    </div>
  );
}

function Kpis({ data }: { data: CropStructure }) {
  const share = cropsSharePct(data);
  return (
    <TileGrid cols={4}>
      <Tile value={`${formatHa(data.arable_ha)} га`} label="пашня хозяйства" />
      <Tile
        value={`${formatHa(data.crops_area_ha)} га`}
        label={share === null ? 'под культурами' : `под культурами · ${share} %`}
      />
      <Tile value={`${formatHa(data.fallow_ha)} га`} label="поля отдыхают" />
      {data.overplanned_ha > 0 ? (
        <Tile
          value={`+${formatHa(data.overplanned_ha)} га`}
          label="план больше пашни"
          tone="warn"
        />
      ) : (
        <Tile
          value={`${formatHa(data.unassigned_ha)} га`}
          label="культура не назначена"
          tone={data.unassigned_ha > 0 ? 'warn' : undefined}
        />
      )}
      <Tile
        value={data.plan_fields_count === null ? '—' : String(data.plan_fields_count)}
        label="полей в плане"
      />
      {/* Валовка считается из техкарт — без права на них плитку не рисуем
          вовсе: прочерк без причины читается как «система не посчитала». */}
      {data.economics_visible && (
        <Tile value={`${formatTons(data.gross_tons)} т`} label="плановая валовка" />
      )}
      <Tile value={String(data.crops_count)} label={`культур в структуре ${data.season_year}`} />
      <Tile
        value={data.prev_season_year ? String(data.prev_season_year) : '—'}
        label="сезон для сравнения"
      />
    </TileGrid>
  );
}

function RotationCard({ data }: { data: CropStructure }) {
  const hasNotes = data.rotation_notes.length > 0;
  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">Севооборот: что система заметила</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <Stack gap={3}>
          {data.rotation_notes.map((note) => (
            <div key={note.code} className="flex gap-3">
              {/* Оба замечания одной тяжести (бэк отдаёт `alert` всегда), но
                  красный destructive тут врал бы: экран объясняет, а не
                  запрещает. Акцент = «посмотри», не «ошибка». */}
              <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-accent-brand" />
              <div className="text-sm">
                <div className="font-medium text-foreground">{note.title}</div>
                <div className="text-muted-foreground">{note.detail}</div>
                {note.field_names.length > 0 && (
                  <div className="text-xs text-muted-foreground">
                    поля: {note.field_names.join(', ')}
                    {note.fields_count > note.field_names.length && ' и другие'}
                  </div>
                )}
              </div>
            </div>
          ))}
          <p className="text-sm text-muted-foreground">
            {rotationBasisNote(data.rotation_basis, hasNotes)}
          </p>
          <p className="text-xs text-muted-foreground">
            Нормативы возврата культуры берутся из справочника культур. Где норматив не
            заполнен, система молчит — придумывать срок за агронома она не должна.
          </p>
        </Stack>
      </CardContent>
    </Card>
  );
}

function FlowCard({ data, year }: { data: CropStructure; year: number }) {
  return (
    <Card>
      <CardHeader className="p-4 pb-3">
        <CardTitle className="text-base">Как структура превращается в деньги</CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <Stack gap={3}>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            <li>
              <span className="text-foreground">Распределение культур</span> — какая
              культура на какой площади и на каких полях.
            </li>
            <li>
              <span className="text-foreground">Структура посевных площадей</span> (этот
              экран) — свод по культурам: площади, доли, валовка, севооборот.
            </li>
            <li>
              <span className="text-foreground">Назначение техкарт</span> — на каждую
              культуру вешается вариант техкарты, появляется ₽/га.
            </li>
            <li>
              <span className="text-foreground">Сводка варианта</span> — валовка × цены
              минус затраты техкарт.
            </li>
            <li>
              <span className="text-foreground">Сравнение вариантов</span> — один уходит
              на утверждение и становится производственной программой {year}.
            </li>
          </ol>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link
                to="/planning/seasons/$year/tech-cards"
                params={{ year: String(year) }}
              >
                Технологические карты
              </Link>
            </Button>
          </div>
          {data.unassigned_ha > 0 && (
            <p className="text-sm text-accent-brand">
              {formatHa(data.unassigned_ha)} га без культуры держат структуру неполной.
              Пока эти поля не распределены, доли считаются от всей пашни, а валовка
              занижена — это честнее, чем прятать нераспределённое из знаменателя.
            </p>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

export function CropStructurePage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const search = useSearch({ strict: false }) as SppSearch;
  const navigate = useNavigate();
  const season = useSeasonByYear(year);

  // Хозяйство — из переключателя слева (AK-866 В-3: область — из
  // переключателя); `?farm=` — только присланная ссылка (AK-897).
  const farmScope = usePlanningFarmScope({
    searchFarm: search.farm,
    onScopeChanged: () =>
      void navigate({
        to: '/planning/seasons/$year/crop-structure',
        params: { year: String(year) },
        search: { ...search, farm: undefined, variant: undefined },
        replace: true,
      }),
    onDeepLinkApplied: () =>
      void navigate({
        to: '/planning/seasons/$year/crop-structure',
        params: { year: String(year) },
        search: { ...search, farm: undefined },
        replace: true,
      }),
  });
  const { farmId } = farmScope;
  const farmName = farmScope.farm?.name ?? null;

  const variants = useSppPlanVariants(farmId, season.seasonId);
  // По умолчанию открываем «план сезона» (is_selected) — именно его защищают.
  const selectedVariant =
    (variants.data ?? []).find((v) => v.id === search.variant) ??
    (variants.data ?? []).find((v) => v.is_selected) ??
    (variants.data ?? [])[0] ??
    null;

  const structure = useCropStructure(selectedVariant?.id ?? null);

  const setSearch = (next: SppSearch) =>
    void navigate({
      to: '/planning/seasons/$year/crop-structure',
      params: { year: String(year) },
      search: { ...search, ...next },
    });

  const seasonResolved = !season.isLoading && !season.isError;

  return (
    // AK-866 В-3: область — из переключателя. Своего выбора хозяйства на
    // экране нет; `POST /scope/set` отсюда зовётся только deep-link'ом
    // `?farm=` и только через `useScopeSetter` (AK-897).
    <AppShell>
      <div className="mx-auto w-full max-w-[1600px] pb-safe-fab">
        <PageHeader
          title={
            season.validYear
              ? `Структура посевных площадей · сезон ${year}`
              : 'Структура посевных площадей'
          }
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            // Средняя крошка ведёт в хаб бюджета сезона (макет v2-00).
            {
              label: season.validYear ? `Бюджет сезона ${year}` : 'Бюджет сезона',
              ...(season.validYear ? { to: `/planning/seasons/${year}/budget` } : {}),
            },
            { label: 'Структура посевных площадей' },
          ]}
          description="Что и на какой площади хозяйство сеет. Отдельной СПП в системе нет: это срез варианта плана — меняете распределение культур, структура пересобирается сама."
        />

        <Stack gap={4} className="mt-4">
          {!season.validYear && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Год в адресе страницы некорректный.
              </CardContent>
            </Card>
          )}

          {season.validYear && season.isLoading && <Skeleton className="h-96 w-full" />}

          {season.validYear && season.isError && (
            <Card>
              <CardContent className="p-4 text-sm text-destructive">
                Не удалось загрузить сезоны. Обновите страницу.
              </CardContent>
            </Card>
          )}

          {season.validYear && seasonResolved && !season.seasonId && !season.card && (
            <SeasonYearOutOfWindowCard year={year} />
          )}
          {season.validYear && seasonResolved && !season.seasonId && !!season.card && (
            <SeasonNotStartedCard year={year} onOpened={() => undefined} />
          )}

          {season.seasonId && (
            <>
              <Stack gap={2}>
                <FarmScopeNotice scope={farmScope} />
                <ChipRow
                  label="Вариант плана:"
                  options={(variants.data ?? []).map((v) => ({
                    id: v.id,
                    name: v.name,
                    badge: v.is_selected ? '· план сезона' : undefined,
                  }))}
                  activeId={selectedVariant?.id ?? null}
                  onPick={(id) => setSearch({ variant: id })}
                  trailing={
                    // Тот же вход, что и на посевном плане: имя варианта
                    // правится там, где оно показано, а не только на «своём»
                    // экране (AK-719).
                    selectedVariant ? (
                      <RenameVariantButton
                        variantId={selectedVariant.id}
                        currentName={selectedVariant.name}
                        existingNames={(variants.data ?? []).map((v) => v.name)}
                      />
                    ) : null
                  }
                />
              </Stack>

              {farmId && variants.isLoading && <Skeleton className="h-24 w-full" />}

              {/* Ошибка ≠ пустота: на ошибке НЕ зовём «создать вариант». */}
              {farmId && variants.isError && (
                <Card>
                  <CardContent className="p-4 text-sm text-destructive">
                    {describeApiError(variants.error)}
                  </CardContent>
                </Card>
              )}

              {farmId && !variants.isLoading && !variants.isError && (variants.data ?? []).length === 0 && (
                <Card>
                  <CardContent className="p-4">
                    <Stack gap={2}>
                      <p className="text-sm text-foreground">
                        У этого хозяйства ещё нет ни одного варианта плана на сезон {year}.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Структура посевных площадей — свод варианта плана, поэтому пока
                        варианта нет, сводить нечего.
                      </p>
                      <div>
                        <CreateVariantButton
                          emptyState
                          farmId={farmId}
                          seasonId={season.seasonId}
                          seasonYear={year}
                          farmName={farmName}
                          existingNames={[]}
                          onCreated={(id) => setSearch({ variant: id })}
                        />
                      </div>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {structure.isLoading && selectedVariant && <Skeleton className="h-96 w-full" />}

              {structure.isError && (
                <Card>
                  <CardContent className="p-4 text-sm text-destructive">
                    {describeApiError(structure.error)}
                  </CardContent>
                </Card>
              )}

              {structure.data && (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={structure.data.is_selected ? 'primary' : 'outline'}>
                      {structure.data.is_selected ? 'план сезона' : 'вариант плана'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {structure.data.farm_name} · {structure.data.scenario_name}
                    </span>
                  </div>

                  <Kpis data={structure.data} />

                  {/* Пашня — знаменатель ВСЕГО экрана. Ноль здесь означает не
                      «система не посчитала», а «полей, которые считаются
                      пашней, нет»: предикат — активное неархивное поле с
                      угодьем, гоняющим севооборот (services/land_area.py).
                      Без этой подписи ноль читается как поломка. */}
                  {structure.data.arable_ha === 0 && (
                    <Card>
                      <CardContent className="p-4 text-sm text-muted-foreground">
                        Пашня считается по полям, у которых в карточке задано угодье с
                        севооборотом («Пашня»), и которые не выведены из оборота. Сейчас таких
                        полей у хозяйства нет — отсюда 0 га, и от него же не считаются доли
                        культур. Проверьте угодье в карточках полей.
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader className="p-4 pb-3">
                      <CardTitle className="text-base">
                        Структура {structure.data.season_year}
                        {structure.data.prev_season_year
                          ? ` против факта ${structure.data.prev_season_year}`
                          : ''}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <Stack gap={3}>
                        <p className="text-sm text-muted-foreground">
                          {planSourceNote(structure.data.plan_source, structure.data.season_year)}
                        </p>
                        {structure.data.overplanned_ha > 0 && (
                          <p className="text-sm text-accent-brand">
                            {overplannedNote(
                              structure.data.overplanned_ha,
                              structure.data.season_year,
                            )}
                          </p>
                        )}
                        {structure.data.items.length > 0 ? (
                          <StructureTable data={structure.data} />
                        ) : (
                          <Stack gap={2}>
                            <p className="text-sm text-muted-foreground">
                              В этом варианте плана пока ни одной культуры. Структура — срез
                              варианта, поэтому заводят культуры там же, где назначают
                              технологии.
                            </p>
                            <div>
                              <Button asChild size="sm">
                                <Link
                                  to="/planning/seasons/$year/plan-variant"
                                  params={{ year: String(year) }}
                                  search={{ variant: selectedVariant?.id }}
                                >
                                  Завести культуры в план
                                </Link>
                              </Button>
                            </div>
                          </Stack>
                        )}
                        {!structure.data.economics_visible && (
                          <p className="text-sm text-muted-foreground">
                            {economicsHiddenNote()}
                          </p>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>

                  <RotationCard data={structure.data} />
                  <FlowCard data={structure.data} year={year} />
                </>
              )}
            </>
          )}
        </Stack>
      </div>
    </AppShell>
  );
}

export default CropStructurePage;
